import { zodResolver } from '@hookform/resolvers/zod';
import { PASSWORD_MIN_LENGTH } from '@iculogbook/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { ApiError, authApi, NetworkError } from '../auth/api.ts';
import { AuthLayout, FormError } from '../auth/AuthLayout.tsx';
import { errorCopy, OFFLINE_COPY } from '../auth/errorCopy.ts';
import { Button, Field, PasswordInput } from '../components/index.ts';

const schema = z.object({
  password: z
    .string()
    .min(
      PASSWORD_MIN_LENGTH,
      `Passwords need to be at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
    ),
});

type Values = z.infer<typeof schema>;

export function ResetPasswordRoute() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { password: '' } });

  const onSubmit = handleSubmit(async (values) => {
    if (token === null) return;
    setFormError(null);
    try {
      await authApi.resetPassword({ token, password: values.password });
      setDone(true);
    } catch (error) {
      setFormError(
        error instanceof NetworkError
          ? OFFLINE_COPY
          : errorCopy(error instanceof ApiError ? error.code : null),
      );
    }
  });

  // Someone opened /reset-password with no token — a truncated link, usually.
  if (token === null) {
    return (
      <AuthLayout
        title="That link is incomplete"
        footer={
          <Link to="/forgot-password" className="text-accent underline hover:text-accent-hover">
            Request a new link
          </Link>
        }
      >
        <p className="text-sm text-muted">
          Reset links are long and some email apps break them across lines. Copy the whole link, or
          request a fresh one.
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        title="Password changed"
        footer={
          <Link to="/login" className="text-accent underline hover:text-accent-hover">
            Sign in
          </Link>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-muted">
          <p>Your password is updated and you have been signed out everywhere.</p>
          <p>Sign in again with the new password.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      footer={
        <Link to="/login" className="text-accent underline hover:text-accent-hover">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />

        <Field
          label="New password"
          help={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A few unrelated words works well.`}
          error={errors.password?.message}
        >
          <PasswordInput autoComplete="new-password" {...register('password')} />
        </Field>

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Set new password
        </Button>

        <p className="text-center text-xs text-faint">
          Changing your password signs you out on every device.
        </p>
      </form>
    </AuthLayout>
  );
}
